import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Link,
  Skeleton,
  Menu,
  MenuItem,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { toast } from "react-toastify";
import { useSocket } from "../../../SocketContext";
import { ROOT_URL } from "../../../api/ApiConfig";
import TicketMessageContainer from "../TicketSectionComponents/CreateTicket/TicketMessageContainer";
import "../TicketSectionComponents/CreateTicket/style.css";
import "../TicketSectionComponents/TicketChatSection/style.css";
import { TV2_COLUMN_HEADER_HEIGHT } from "../../../constants/ticketV2Constants";
import { IconDetailsSplit24 } from "./TicketV2Icons";
import "./ticketV2MessageOverrides.css";
import EditSubjectPopup from "./EditSubjectPopup";

function downloadConversationText(ticket, messages) {
  const shortId = ticket?._id?.slice(-6) || "ticket";
  const lines = [];
  lines.push(`Ticket #${shortId}`);
  lines.push(`Subject: ${ticket?.subject || "N/A"}`);
  lines.push(`Status: ${ticket?.status || "N/A"}`);
  lines.push("");
  lines.push("--- Messages ---");
  lines.push("");
  (messages || []).forEach((m) => {
    const when = m.created_at || m.createdAt || "";
    const who =
      m.sender ||
      m.agent?.full_name ||
      (m.type === "user" ? "Customer" : m.type === "agent" ? "Agent" : "System");
    let body = m.message || m.content || "";
    if (m.type === "events" && m.meta) {
      body = `[${m.meta.type || "event"}] ${JSON.stringify(m.meta.value || "")}`;
    }
    if (body) lines.push(`[${when}] ${who}: ${body}`);
  });
  const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `conversation-${shortId}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

const headerRowSx = {
  height: TV2_COLUMN_HEADER_HEIGHT,
  minHeight: TV2_COLUMN_HEADER_HEIGHT,
  boxSizing: "border-box",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  px: 2,
  borderBottom: "1px solid #E5E9EC",
};

function ChatThreadSkeleton() {
  return (
    <Stack gap={2.25} sx={{ width: "100%", px: 2, py: 2 }}>
      {[1, 2, 3, 4].map((k) => (
        <Stack key={k} direction="row" gap={1.5} alignItems="flex-start" sx={k % 2 === 0 ? { justifyContent: "flex-end" } : {}}>
          {k % 2 !== 0 && <Skeleton variant="circular" width={32} height={32} />}
          <Stack gap={0.75} sx={{ maxWidth: "70%" }}>
            <Skeleton variant="rounded" width={k % 2 === 0 ? 220 : 260} height={72} sx={{ borderRadius: "8px" }} />
            <Skeleton width={48} height={12} sx={{ alignSelf: k % 2 === 0 ? "flex-end" : "flex-start" }} />
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

const noop = () => {};

function TicketChat({
  ticket,
  ticketMessages,
  ticketId,
  ticketRaw,
  messagesLoading,
  ticketDetailLoading,
  detailsOpen,
  onToggleDetails,
  onSubjectSaved,
}) {
  const socket = useSocket();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [editSubjectOpen, setEditSubjectOpen] = useState(false);
  const [transcriptMessages, setTranscriptMessages] = useState([]);
  const [customerEmail, setCustomerEmail] = useState(ticketRaw?.customerEmail || ticket?.customerEmail || "");
  const [customerName, setCustomerName] = useState(ticketRaw?.customerName || ticket?.customerName || "");

  const ticketForMessages = ticketRaw && Object.keys(ticketRaw).length ? ticketRaw : ticket?.raw || ticket;
  const ticketStatus = String(ticketForMessages?.status || ticket?.status || "open").toLowerCase();

  useEffect(() => {
    setCustomerEmail(ticketRaw?.customerEmail || ticket?.customerEmail || "");
    setCustomerName(ticketRaw?.customerName || ticket?.customerName || "");
  }, [ticketRaw?.customerEmail, ticketRaw?.customerName, ticket?.customerEmail, ticket?.customerName]);

  const fetchTranscriptMessages = useCallback(async () => {
    const chatRoom = ticketForMessages?.chatRoom;
    if (!chatRoom) {
      setTranscriptMessages([]);
      return;
    }
    try {
      const response = await fetch(`${ROOT_URL}/message/get-message/${chatRoom}`);
      const res = await response.json();
      if (res.status === 200 && Array.isArray(res.data)) {
        setTranscriptMessages(res.data);
      } else if (res.success && Array.isArray(res.messages)) {
        setTranscriptMessages(res.messages);
      } else {
        setTranscriptMessages([]);
      }
    } catch {
      setTranscriptMessages([]);
    }
  }, [ticketForMessages?.chatRoom]);

  useEffect(() => {
    fetchTranscriptMessages();
  }, [fetchTranscriptMessages]);

  const subjectForEdit = ticket?.subject || ticketForMessages?.subject || "";
  const subject = subjectForEdit || "—";
  const shortId = ticket?.id || (ticketId ? ticketId.slice(-6) : "—");
  const createdByName = ticket?.createdBy?.name || "—";
  const createdByEmail = ticket?.createdBy?.email || "—";
  const showMessageSkeleton = Boolean(messagesLoading && (!ticketMessages || ticketMessages.length === 0));

  const handleOpenMenu = (e) => {
    setMenuAnchor(e.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const handleExportConversation = () => {
    handleCloseMenu();
    try {
      downloadConversationText(ticketForMessages || ticket, ticketMessages);
      toast.success("Conversation downloaded");
    } catch {
      toast.error("Could not export conversation");
    }
  };

  const menuPaperSx = {
    width: 176,
    borderRadius: "8px",
    border: "1px solid #D5DCE1",
    bgcolor: "#FFF",
    boxShadow: "0 15px 20px 0 rgba(6, 21, 34, 0.05)",
    mt: 0.5,
    overflow: "hidden",
    "& .MuiList-root": {
      py: 1,
    },
  };

  const menuItemSx = {
    fontSize: 14,
    fontStyle: "normal",
    fontWeight: 400,
    fontFamily: '"Inter", sans-serif',
    lineHeight: "20px",
    color: "#556575",
    py: 1,
    px: 2,
    minHeight: "auto",
    "&:hover": {
      color: "#071623",
      bgcolor: "transparent",
    },
  };

  return (
    <Box
      component="section"
      sx={{
        flex: "1 1 592px",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ ...headerRowSx, justifyContent: "flex-start" }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 0.25,
          }}
        >
          {ticketDetailLoading ? (
            <>
              <Skeleton width="75%" height={22} />
              <Skeleton width={96} height={14} />
            </>
          ) : (
            <>
              <Typography
                sx={{
                  color: "#061522",
                  fontFamily: '"Inter", sans-serif',
                  fontSize: "16px",
                  fontWeight: 500,
                  lineHeight: "18px",
                }}
              >
                {subject}
              </Typography>
              <Typography
                sx={{
                  color: "#405261",
                  fontFamily: '"Inter", sans-serif',
                  fontSize: "10px",
                  fontWeight: 400,
                  lineHeight: "18px",
                }}
              >
                #{shortId}
              </Typography>
            </>
          )}
        </Box>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
          <IconButton
            size="small"
            aria-label="Ticket actions"
            aria-haspopup="true"
            aria-expanded={Boolean(menuAnchor)}
            onClick={handleOpenMenu}
            sx={{ color: "#405261" }}
          >
            <MoreHorizIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleCloseMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                elevation: 0,
                sx: menuPaperSx,
              },
            }}
            MenuListProps={{ dense: true, sx: { py: 1 } }}
          >
            <MenuItem
              onClick={() => {
                handleCloseMenu();
                setEditSubjectOpen(true);
              }}
              sx={menuItemSx}
            >
              Edit Subject
            </MenuItem>
            <MenuItem onClick={handleExportConversation} sx={menuItemSx}>
              Export Conversation
            </MenuItem>
          </Menu>

          {!detailsOpen && (
            <IconButton
              size="small"
              onClick={onToggleDetails}
              aria-label="Show details panel"
              sx={{ color: "#405261" }}
            >
              <IconDetailsSplit24 />
            </IconButton>
          )}
        </Stack>
      </Stack>

      <Box
        className="ticketv2-created-trail"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0,
          px: 2,
          py: 2.5,
          pb:0,
          flexShrink: 0,
        }}
      >
        <Box className="ticketv2-created-trail__line" aria-hidden />
        {ticketDetailLoading ? (
          <Skeleton width={280} height={16} sx={{ mx: 1.25, flexShrink: 0 }} />
        ) : (
          <Typography
            component="p"
            className="ticketv2-created-trail__text"
            sx={{
              m: 0,
              flexShrink: 0,
              whiteSpace: "nowrap",
              fontSize: 12,
              fontWeight: 400,
              lineHeight: "16px",
              fontFamily: '"Inter", sans-serif',
              color: "#8B97A2",
              bgcolor: "#FFF",
              px: 1.25,
              py: 0.5,
            }}
          >
            Ticket created by{" "}
            <Link
              component="span"
              underline="hover"
              sx={{ color: "#148DFD", cursor: "default" }}
            >
              {createdByName}
            </Link>{" "}
            via email{" "}
            <Link
              component="span"
              underline="hover"
              sx={{ color: "#148DFD", cursor: "default" }}
            >
              ({createdByEmail})
            </Link>
          </Typography>
        )}
        <Box className="ticketv2-created-trail__line" aria-hidden />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {showMessageSkeleton ? (
          <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
            <ChatThreadSkeleton />
          </Box>
        ) : (
          <div
            className="TicketMessageInput-Container-MainScreen ticketv2-ticket-chat"
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <TicketMessageContainer
              setSelectedState={noop}
              setActiveTicketMerge={noop}
              messages={ticketMessages || []}
              createdAt={ticketForMessages?.created_at || ticketForMessages?.createdAt}
              ticketStatus={ticketStatus}
              socketInstance={socket}
              assignedAgent={ticketForMessages?.assignee?.full_name}
              transcriptMessages={transcriptMessages}
              ticket={ticketForMessages}
              customerEmail={customerEmail}
              customerName={customerName}
              composerVariant="tv2"
            />
          </div>
        )}
      </Box>

      <EditSubjectPopup
        open={editSubjectOpen}
        onClose={() => setEditSubjectOpen(false)}
        ticketId={ticketId}
        initialSubject={subjectForEdit}
        onSaved={onSubjectSaved}
      />
    </Box>
  );
}

export default TicketChat;
